<script setup lang="ts">
/**
 * **画布** —— 我们自己的节点图渲染与交互层（Vue Flow 的替代品）。
 *
 * ## 它管什么
 *
 * ```text
 *   ① 视口：滚轮缩放（指哪儿缩哪儿）、中键拖 / 双指拖平移、fitView / 定位
 *   ② 节点层：按文档坐标摆放（`transform`），拖动、抬升
 *   ③ 边层：SVG 折线 → 三次贝塞尔（端点来自**量出来的端口位置**）
 *   ④ 交互：拖节点、框选、点选、起线（连接拖拽）
 *   ⑤ 输出：把变更**提议**给宿主（`nodes-change` / `edges-change`）+ 事件
 * ```
 *
 * ## 三条不能破的约定
 *
 * **① 文档是宿主的。** 这个组件**没有**节点数组的副本：`:nodes` 进来、
 * 变更事件出去（`documentSync.ts` 的设计）。所以"两个窗口看同一份文档"
 * 不需要同步逻辑 —— 结构上就只有一份。
 *
 * **② DOM 契约照 `graphDom.ts` 来**（class 名 + `data-*`）。全仓库 26 处
 * 命中测试 / 悬停 / 剪线都按那套属性找元素；换引擎时"照着重画一遍"
 * 就是这一层存在的意义。
 *
 * **③ 交互数学在纯函数里。** 指针状态机在 `paneInteraction.ts`
 * （有 30 多条断言），视口换算在 `viewport.ts`，边形状在 `edgeRouting.ts`。
 * 这个文件只做**接线**：DOM 事件 → 纯函数 → 命令 → 事件。
 *
 * ## ⚠️ 和原来 `<VueFlow>` 的几处**有意的差别**
 *
 * | 原来（Vue Flow） | 现在 | 为什么 |
 * |---|---|---|
 * | 内置 d3-zoom / d3-drag | 我们自己的指针状态机 | 双指、长按、划痕本来就是宿主自己写的；再叠一层 d3 只会互相抢事件（历史上有好几处 `stopPropagation` 的补丁） |
 * | `isValidConnection` 会**复检每一条已存在的边** | 只在**松手那一刻**用它判定落点 | 原来那个行为踩过坑：一旦判定里含"是否已存在"，所有边会被整批判非法（宿主源码里有记录）。我们不做那种复检 |
 * | MiniMap 自带 d3-zoom | 手势完全归宿主（`useMiniMapGesture`） | 触摸端曾经因为两套缩放打架而"完全拖不动" |
 * | 节点拖动的"每帧就地写 + 松手收敛"由引擎内部处理 | 我们照发 `dragging: true/false` 的位置变更 | 宿主的写入口就是按这个判据分流（`isDraggingPositionChange`），行为保持不变 |
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import NpCanvasBackground from './NpCanvasBackground.vue'
import NpCanvasMiniMap from './NpCanvasMiniMap.vue'
import NpCanvasNode from './NpCanvasNode.vue'
import { createCanvasStore } from './canvasState'
import {
  IDLE_INTERACTION,
  beginInteraction,
  cancelInteraction,
  endInteraction,
  moveInteraction,
  normalizedBox,
  rectContainsPoint,
} from './paneInteraction'
import type { CanvasCommand, CanvasHit, CanvasInteraction, CanvasPointer } from './paneInteraction'
import { DEFAULT_VIEWPORT, screenToFlow, zoomAtScreen } from './viewport'
import type { ZoomLimits } from './viewport'
import { bezierPath, connectorPoints } from './edgeRouting'
import type { FlowConnection, FlowEdge, FlowNode } from '../flowTypes'
import type { FlowEdgeChange, FlowNodeChange } from '../flowChanges'
import type { Size } from './handleGeometry'
import type { Point } from '../../types/geometry'
import type { Viewport } from '../minimapGesture'
/* 画布自己的样式 —— 合约见 `graphDom.ts`（选择器名就是从那来的） */
import './np-canvas.css'

/**
 * **这个节点的叠放次序**（模型上的 `z`，见 `DesignNode.z`）。
 *
 * ⚠️ 从 `node.data.model` 上读，而不是让宿主再传一份 —— 那份数据本来就在
 * 每个节点自己身上（画布只拿它算一个 `z-index`，不认识它的语义）。
 * 老文档 / 没有这个字段的节点 → 0（和第一版的行为一致）。
 */
function zIndexOf(node: FlowNode): number {
  const model = (node.data as { model?: { z?: number } } | undefined)?.model
  const value = model?.z
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

const props = withDefaults(
  defineProps<{
    nodes?: FlowNode[]
    edges?: FlowEdge[]
    minZoom?: number
    maxZoom?: number
    defaultViewport?: Viewport
    /** **左键**拖拽能不能平移画布（默认 false：左键留给框选；中键始终平移） */
    panOnDrag?: boolean
    /** 松手落点离端口多远还算命中（屏幕像素） */
    connectionRadius?: number
    /** 结构校验（宿主的 `isValidConnection`）——只在松手那一刻问一次 */
    isValidConnection?: (connection: FlowConnection) => boolean
    /** 底纹 */
    background?: boolean
    backgroundGap?: number
    backgroundSize?: number
    backgroundDot?: string
    /** 小地图（`null` / 省略 = 不渲染） */
    minimap?: boolean
    minimapSize?: Size
    minimapNodeColor?: (node: FlowNode) => string
    minimapNodeStroke?: (node: FlowNode) => string
    minimapMaskColor?: string
    /** 小地图折叠按钮要用的角（与 `.np-graph[data-np-minimap-corner]` 一致） */
    minimapCorner?: 'tl' | 'tr' | 'bl' | 'br'
  }>(),
  {
    nodes: () => [],
    edges: () => [],
    minZoom: 0.2,
    maxZoom: 3,
    defaultViewport: () => ({ ...DEFAULT_VIEWPORT }),
    panOnDrag: false,
    connectionRadius: 30,
    background: true,
    backgroundGap: 22,
    backgroundSize: 1.4,
    minimap: false,
    minimapSize: () => ({ width: 200, height: 130 }),
    minimapCorner: 'br',
  },
)

const emit = defineEmits<{
  (e: 'nodes-change', changes: FlowNodeChange[]): void
  (e: 'edges-change', changes: FlowEdgeChange[]): void
  (e: 'viewport-change', viewport: Viewport): void
  (e: 'init', store: unknown): void
  (e: 'connect', connection: FlowConnection): void
  (e: 'connect-start', payload: { event: PointerEvent; nodeId: string; handleId: string; handleType: 'source' | 'target' }): void
  (e: 'connect-end'): void
  (e: 'node-click', payload: { node: FlowNode; event: PointerEvent }): void
  (e: 'pane-click', event: PointerEvent): void
  (e: 'node-context-menu', payload: { node: FlowNode; event: MouseEvent }): void
  (e: 'pane-context-menu', event: MouseEvent): void
  (e: 'selection-context-menu', event: MouseEvent): void
  (e: 'edge-context-menu', payload: { edge: FlowEdge; event: MouseEvent }): void
  (e: 'edge-double-click', payload: { edge: FlowEdge; event: MouseEvent }): void
  (e: 'node-drag-start', payload: { node: FlowNode; nodes: FlowNode[] }): void
  (e: 'node-drag', payload: { node: FlowNode; nodes: FlowNode[] }): void
  (e: 'node-drag-stop', payload: { node: FlowNode; nodes: FlowNode[] }): void
  /*
   * 小地图上的指针事件**转出去给宿主**。
   *
   * ⚠️ 小地图的手势（单指拖动平移、双指捏合缩放）是宿主自己的逻辑
   * （`useMiniMapGesture`，它从 DOM 读 viewBox 与元素尺寸来换算）。
   * 画布不接管 —— 重叠两套缩放手势正是当年"小地图完全拖不动"的原因。
   */
  (e: 'minimap-pointer-down', event: PointerEvent): void
  (e: 'minimap-pointer-move', event: PointerEvent): void
  (e: 'minimap-pointer-up', event: PointerEvent): void
  (e: 'minimap-pointer-cancel', event: PointerEvent): void
}>()

const hostEl = ref<HTMLElement | null>(null)
const interaction = shallowRef(IDLE_INTERACTION)
const hostSize = ref<Size>({ width: 0, height: 0 })

const { viewport, connection, selectionActive, store } = createCanvasStore({
  root: () => hostEl.value,
  nodes: () => props.nodes,
  edges: () => props.edges,
  emitNodeChanges: (changes) => emit('nodes-change', changes),
  emitEdgeChanges: (changes) => emit('edges-change', changes),
  emitViewportChange: (next) => emit('viewport-change', next),
  minZoom: props.minZoom,
  maxZoom: props.maxZoom,
})

/* ══ 视口 ══════════════════════════════════════════════════════════ */

const limits = computed<ZoomLimits>(() => ({ minZoom: props.minZoom, maxZoom: props.maxZoom }))

const viewportStyle = computed(() => ({
  transform: `translate(${viewport.value.x}px, ${viewport.value.y}px) scale(${viewport.value.zoom})`,
}))

function measureHost(): void {
  const rect = hostEl.value?.getBoundingClientRect()
  if (rect) hostSize.value = { width: rect.width, height: rect.height }
}

function onWheel(event: WheelEvent): void {
  const rect = hostEl.value?.getBoundingClientRect()
  if (!rect) return
  /*
   * ⚠️ 指针位置可能是 `undefined`（有些环境 / 合成事件不带 clientX）——
   * 不兜的话坐标算出来是 NaN，视口会被夹成 (0,0)：画面**悄悄跳到原点**。
   * 兜底取容器中心，语义上等于"绕中间缩放"，不会毁掉用户当前看的区域。
   */
  const clientX = Number.isFinite(event.clientX) ? event.clientX : rect.left + rect.width / 2
  const clientY = Number.isFinite(event.clientY) ? event.clientY : rect.top + rect.height / 2
  const local = { x: clientX - rect.left, y: clientY - rect.top }
  /*
   * ⚠️ 归一化滚轮增量：`deltaMode` 在不同浏览器 / 设备上不一样
   * （0 = 像素、1 = 行、2 = 页）。直接用 `deltaY` 会让"某些鼠标滚一格
   * 缩放 10 倍"—— 这是滚轮处理最常见的坑。
   */
  const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1
  const delta = event.deltaY * unit
  const factor = Math.exp(-delta / 400)
  const next = zoomAtScreen(viewport.value, factor, local, limits.value)
  if (next !== viewport.value) store.setViewport(next)
}

/* ══ 指针：拖节点 / 框选 / 平移 / 点选 ═══════════════════════════════ */

function pointerOf(event: PointerEvent): CanvasPointer {
  const rect = hostEl.value?.getBoundingClientRect()
  const host = rect
    ? { x: event.clientX - rect.left, y: event.clientY - rect.top }
    : { x: event.clientX, y: event.clientY }
  return {
    host,
    flow: screenToFlow(viewport.value, host),
    modifiers: {
      additive: event.metaKey || event.ctrlKey || event.shiftKey,
      panMode: props.panOnDrag && !event.shiftKey && !event.metaKey && !event.ctrlKey,
      middle: event.button === 1,
      right: event.button === 2,
    },
  }
}

/**
 * 落点上有什么 —— DOM 判定（这一层知道元素，纯函数不知道）。
 *
 * ⚠️ **顺序是这条函数的全部要点**：先问"这是不是交互件"，再问"在不在节点里"。
 *
 * 第一版把节点判定放在了前面，于是**节点里的任何东西都被当成"拖节点"** ——
 * 数值控件上左右拖动改值、变参行拖动排序，全都会把节点拖走（真机反馈）。
 * 视觉层早就给这些地方标了 `.nodrag`（那是它和画布之间的约定），
 * 而 `.nodrag` 的判定被压在了后面，等于没写。
 *
 * 换句话说：`.nodrag` 是**排除项**，排除项必须在包含项之前判 ——
 * 反过来写，包含项永远赢。
 */
function hitAt(event: PointerEvent): CanvasHit {
  const target = event.target as HTMLElement | null
  if (!target) return { kind: 'pane' }

  /*
   * ① 交互件（控件 / 行内按钮 / 端口 / 菜单 / 输入框…）：画布**不接管**。
   *    它们自己的手势（改值、排序、起线）优先级更高。
   */
  if (
    target.closest(
      '.nodrag, .np-graph__handle, .np-menu, .np-log, input, textarea, select, button, a, [contenteditable="true"]',
    )
  ) {
    return { kind: 'other' }
  }

  /* ② 节点 → 拖节点 */
  const nodeEl = target.closest<HTMLElement>('.np-graph__node')
  const nodeId = nodeEl?.dataset.id
  if (nodeId) return { kind: 'node', id: nodeId }

  /* ③ 连线 → 选中 / 菜单 */
  const edgeEl = target.closest<HTMLElement>('.np-graph__edge')
  const edgeId = edgeEl?.dataset.id
  if (edgeId) return { kind: 'edge', id: edgeId }

  /* ④ 其余就是空白画布 */
  return { kind: 'pane' }
}

/*
 * ⚠️ 画布**不监听任何键盘事件** —— 这条是刻意的，而且踩过。
 *
 * 我在这里实现过"按住空格 = 临时平移"（`spaceHeld` + 两个 window 监听器），
 * 而 `useHotkeys.onSpaceKey` 里写着：那条链**在 Vue Flow 时代就被删了** ——
 * 「它原来还兼职'临时平移修饰键'……但那条链从来没生效过（实测反馈
 * 『空格 + 拖：没反应』）」。也就是说我重新发明了一个**从来没工作过**的功能，
 * 还搭进去两个全局监听器（而全局键盘监听正是最容易和快捷键打架的东西）。
 *
 * 现在的分工：
 *   · **空格** 归宿主的 `useHotkeys`（它只做一件事：别让页面滚动）；
 *   · **快捷键** 归宿主的 `useShortcuts`（`shouldIgnoreEvent` 会跳过输入框）；
 *   · 画布只认**指针**：中键拖 / 双指拖 = 平移，左键拖空白 = 框选。
 */

/**
 * 执行命令。
 *
 * ⚠️ `source` 是**命令产生时的那个交互状态**，默认取当前值。
 * 松手时必须显式传进来：`onPointerUp` 会把 `interaction` 重置成 idle，
 * 而 `drag-stop` 要用"拖的是哪几个节点"—— 用重置后的状态会拿到空数组，
 * 于是**松手那一刻的收敛发布根本不会发生**（新窗口永远看不到最后的位置）。
 * 这个是 `verify:canvas-dom` 抓出来的。
 */
function runCommands(
  commands: readonly CanvasCommand[],
  source: CanvasInteraction = interaction.value,
): void {
  for (const command of commands) {
    switch (command.type) {
      case 'pan': {
        store.setViewport({ ...viewport.value, x: viewport.value.x + command.dx, y: viewport.value.y + command.dy })
        break
      }
      case 'move-nodes': {
        /*
         * ⚠️ `dragging: true` —— 宿主的写入口**按这个判据分流**：
         * 拖动中的位置变更就地写（不发布），松手那一刻（dragging: false）
         * 才走发布路径收敛。写反了的后果是"节点完全拖不动"（实测过）。
         */
        emit(
          'nodes-change',
          command.moves.map((move) => ({
            id: move.id,
            type: 'position' as const,
            position: move.position,
            dragging: true,
          })),
        )
        const primary = command.moves[0]?.id
        if (primary) {
          const node = props.nodes.find((item) => item.id === primary)
          if (node) emit('node-drag', { node: { ...node, position: command.moves[0]!.position }, nodes: props.nodes })
        }
        break
      }
      case 'drag-start': {
        const primary = command.nodeIds[0]
        const node = primary ? props.nodes.find((item) => item.id === primary) : undefined
        if (node) emit('node-drag-start', { node, nodes: props.nodes })
        break
      }
      case 'drag-stop': {
        /*
         * 松手：把**拖动过的那些节点**的当前位置再发一遍（`dragging: false`）——
         * 这一步才是"发布给所有窗口"的那一次。
         */
        const moved = source.nodeIds
          .map((id) => props.nodes.find((node) => node.id === id))
          .filter((node): node is FlowNode => !!node)
        if (moved.length) {
          emit(
            'nodes-change',
            moved.map((node) => ({
              id: node.id,
              type: 'position' as const,
              position: node.position,
              dragging: false,
            })),
          )
        }
        const primary = source.nodeIds[0]
        const node = primary ? props.nodes.find((item) => item.id === primary) : undefined
        if (node) emit('node-drag-stop', { node, nodes: props.nodes })
        break
      }
      case 'select-in-box': {
        const box = command.box
        const inside = props.nodes.filter((node) => {
          const size = store.dimensionsOf(node.id) ?? { width: 0, height: 0 }
          return rectsOverlap(box, {
            x: node.position.x,
            y: node.position.y,
            width: size.width,
            height: size.height,
          })
        })
        const ids = new Set(inside.map((node) => node.id))
        const changes: FlowNodeChange[] = []
        for (const node of props.nodes) {
          const should = command.additive ? Boolean(node.selected) || ids.has(node.id) : ids.has(node.id)
          if (Boolean(node.selected) !== should) {
            changes.push({ id: node.id, type: 'select', selected: should })
          }
        }
        if (changes.length) emit('nodes-change', changes)
        break
      }
      case 'click': {
        /* ⚠️ 原始事件不走命令（纯函数模块不碰 DOM 类型），用 lastEvent 带过来 */
        handleClick(command.hit, command.flow, command.modifiers.additive, lastEvent ?? undefined)
        break
      }
    }
  }
}

/** 框选相交（`paneInteraction.rectsIntersect` 的同款，避免多一次 import 抖动） */
function rectsOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  return !(a.x + a.width < b.x || b.x + b.width < a.x || a.y + a.height < b.y || b.y + b.height < a.y)
}

function selectionOf(additive: boolean, id: string): boolean[] | null {
  const node = props.nodes.find((item) => item.id === id)
  if (!node) return null
  const currentlySelected = props.nodes.filter((item) => item.selected).map((item) => item.id)
  const target = additive
    ? [...new Set([...currentlySelected, id])]
    : currentlySelected.includes(id) && currentlySelected.length > 1
      ? currentlySelected
      : [id]
  return props.nodes.map((item) => target.includes(item.id))
}

function selectNodes(flags: boolean[]): void {
  const changes: FlowNodeChange[] = []
  props.nodes.forEach((node, index) => {
    const should = flags[index] ?? false
    if (Boolean(node.selected) !== should) {
      changes.push({ id: node.id, type: 'select', selected: should })
    }
  })
  if (changes.length) emit('nodes-change', changes)
}

function selectEdges(ids: readonly string[]): void {
  const wanted = new Set(ids)
  const changes: FlowEdgeChange[] = []
  for (const edge of props.edges) {
    const should = wanted.has(edge.id)
    if (Boolean(edge.selected) !== should) {
      changes.push({ id: edge.id, type: 'select', selected: should })
    }
  }
  if (changes.length) emit('edges-change', changes)
}

function handleClick(hit: CanvasHit, _flow: Point, additive: boolean, event?: PointerEvent): void {
  if (hit.kind === 'node') {
    const flags = selectionOf(additive, hit.id)
    if (flags) selectNodes(flags)
    selectEdges([])
    const node = props.nodes.find((item) => item.id === hit.id)
    if (node && event) emit('node-click', { node, event })
    return
  }
  if (hit.kind === 'edge') {
    /* 连线永远是单选（多选连线没有意义：删除 / 静音都是逐条的） */
    selectEdges([hit.id])
    const edge = props.edges.find((item) => item.id === hit.id)
    if (edge && event) emit('edge-context-menu', { edge, event: event as unknown as MouseEvent })
    return
  }
  /*
   * ⚠️ 顺序：先发 `pane-click`、**再**清选中 —— 和原来引擎的行为一致。
   * 宿主的注释里记着这条：`paneClick` 触发时选中"还没清"，
   * 所以它那边无条件清 `activeNodeId`；顺序反了会变成"点两次才关得掉面板"。
   */
  if (event) emit('pane-click', event)
  const nodeChanges: FlowNodeChange[] = props.nodes
    .filter((node) => node.selected)
    .map((node) => ({ id: node.id, type: 'select', selected: false }))
  if (nodeChanges.length) emit('nodes-change', nodeChanges)
  selectEdges([])
}

function onPointerDown(event: PointerEvent): void {
  /* 只接左键 / 中键；右键交给菜单 */
  if (event.button !== 0 && event.button !== 1) return
  const hit = hitAt(event)
  /*
   * ⚠️⚠️ 这里**故意不调 `preventDefault()`** —— 这是踩过两次的地方。
   *
   * 它在 `pointerdown` 上的默认行为**不只是**"开始选中文字"，还包括
   * **焦点转移**（把焦点从上一个元素挪走、并触发它的 `blur`）。
   * 一起拦掉的后果是两件事同时坏，而且看起来毫不相干：
   *
   * ```text
   *   ① 改名字 / 改标签的输入框**永远不 blur** → 编辑态关不掉，只能按回车
   *      （真机反馈：「双击标题改名需要按回车才会关闭」）
   *   ② 焦点一直留在输入框里 → 快捷键被 `shouldIgnoreEvent` 全部忽略
   *      （真机反馈：「快捷键没效果了」——
   *        它判的是 `event.target` 是不是 input/textarea）
   * ```
   *
   * 而"不选中文字"这件事**由 CSS 负责**（`.np-graph__canvas { user-select: none }`，
   * 见 `np-canvas.css`），"不触发原生拖放"由模板上的 `@dragstart.prevent` 负责 ——
   * 两层都不碰焦点，所以能各管各的。
   *
   * 一句话：**要拦"选中"和"拖放"，不要顺手拦"焦点"**。
   */
  void hit
  const pointer = pointerOf(event)
  const positions: Record<string, Point> = {}
  for (const node of props.nodes) positions[node.id] = node.position
  const result = beginInteraction(pointer, hit, {
    selectedIds: props.nodes.filter((node) => node.selected).map((node) => node.id),
    positions,
  })
  interaction.value = result.state
  selectionActive.value = result.state.phase === 'selecting'
  if (result.state.phase === 'dragging' || result.state.phase === 'selecting' || result.state.phase === 'panning') {
    hostEl.value?.setPointerCapture?.(event.pointerId)
  }
  lastHit = hit
  lastEvent = event
  runCommands(result.commands)
}

/** 松手时判定"点在什么上"要复用它（按下时判定过一次） */
let lastHit: CanvasHit = { kind: 'pane' }
/** 原始指针事件（点击命令要把它转给宿主的事件处理器） */
let lastEvent: PointerEvent | null = null

function onPointerMove(event: PointerEvent): void {
  if (interaction.value.phase === 'idle') return
  const pointer = pointerOf(event)
  const result = moveInteraction(interaction.value, pointer)
  interaction.value = result.state
  selectionActive.value = result.state.phase === 'selecting'
  runCommands(result.commands)
}

function onPointerUp(event: PointerEvent): void {
  const ended = interaction.value
  if (ended.phase === 'idle') return
  const pointer = pointerOf(event)
  lastEvent = event
  const result = endInteraction(ended, pointer, lastHit)
  interaction.value = IDLE_INTERACTION
  selectionActive.value = false
  /* ⚠️ 传 `ended`：`drag-stop` 要用"拖的是哪几个节点"（重置后就没了） */
  runCommands(result.commands, ended)
  hostEl.value?.releasePointerCapture?.(event.pointerId)
}

function onPointerCancel(): void {
  const result = cancelInteraction(interaction.value)
  interaction.value = IDLE_INTERACTION
  selectionActive.value = false
  runCommands(result.commands)
}

/* ══ 起线（连接拖拽）═══════════════════════════════════════════════ */

type HandleUnder = { nodeId: string; handleId: string; type: 'source' | 'target' } | null

/**
 * 指针下（或附近 `connectionRadius` 内）的端口。
 *
 * ## ⚠️ 半径兜底必须**避开"接不了的那个"**
 *
 * 端口在一行行上，行高只有 ~28px —— 松手差几个像素时，"最近的端口"
 * 完全可能是**相邻那一行**的。而如果那个端口恰好是**已经连过的那一个**，
 * 老实现会：取最近的 → `isValidConnection` 判非法 → **悄悄不发 `connect`**
 * → 宿主以为是"落在空白处" → 弹候选菜单。
 *
 * 真机反馈正是这个：「连线后，a 的输出不能继续往 b 的**其他**可链接端口连线了
 * （会有提醒，但是连不上）」—— 第一个端口连上之后，它的邻居就被它挡住了。
 *
 * 所以兜底要在半径内的候选里**优先挑宿主认合法的那个**：
 *
 * ```text
 *   ① 指针正下方就是端口 → 用它（哪怕非法：那是用户明确点的，交给宿主提示）
 *   ② 否则在半径内按距离排序，先找**宿主判合法**的那个
 *   ③ 都不合法 → 用最近的（让宿主照常给提示，而不是静默无事发生）
 * ```
 */
function handleAt(clientX: number, clientY: number, origin?: HandleUnder): HandleUnder {
  const direct = pickHandle(
    (document.elementFromPoint(clientX, clientY) as HTMLElement | null)?.closest<HTMLElement>(
      '.np-graph__handle',
    ),
  )
  if (direct) return direct

  const rect = hostEl.value?.getBoundingClientRect()
  if (!rect) return null
  const radius = props.connectionRadius
  const candidates: { hit: NonNullable<HandleUnder>; distance: number }[] = []
  for (const el of hostEl.value?.querySelectorAll<HTMLElement>('.np-graph__handle') ?? []) {
    const candidate = pickHandle(el)
    if (!candidate) continue
    const box = el.getBoundingClientRect()
    const distance = Math.hypot(box.left + box.width / 2 - clientX, box.top + box.height / 2 - clientY)
    if (distance > radius) continue
    candidates.push({ hit: candidate, distance })
  }
  if (candidates.length === 0) return null
  candidates.sort((a, b) => a.distance - b.distance)

  if (origin && props.isValidConnection) {
    const usable = candidates.find((candidate) => {
      const connection = buildConnection(origin, candidate.hit)
      return !!connection && props.isValidConnection!(connection)
    })
    if (usable) return usable.hit
  }
  return candidates[0]!.hit
}

/** 一个端口元素 → 落点信息（不合法 / 不可连的一律 null） */
function pickHandle(el: HTMLElement | null | undefined): HandleUnder {
  if (!el || el.dataset.connectable === 'false') return null
  const nodeId = el.dataset.nodeid
  const handleId = el.dataset.handleid
  if (!nodeId || !handleId) return null
  return {
    nodeId,
    handleId,
    type: el.classList.contains('is-target') ? 'target' : 'source',
  }
}

function onHandlePointerDown(event: PointerEvent): boolean {
  if (event.button !== 0) return false
  const el = (event.target as HTMLElement | null)?.closest<HTMLElement>('.np-graph__handle')
  if (!el || el.dataset.connectable === 'false') return false
  const nodeId = el.dataset.nodeid
  const handleId = el.dataset.handleid
  if (!nodeId || !handleId) return false
  const type: 'source' | 'target' = el.classList.contains('is-target') ? 'target' : 'source'
  const rect = el.getBoundingClientRect()
  const hostRect = hostEl.value?.getBoundingClientRect()
  const pointer = hostRect
    ? screenToFlow(viewport.value, { x: rect.left + rect.width / 2 - hostRect.left, y: rect.top + rect.height / 2 - hostRect.top })
    : null
  store.startConnection({ nodeId, id: handleId, type, position: el.dataset.handlepos })
  connection.value = { ...connection.value!, pointer }
  emit('connect-start', { event, nodeId, handleId, handleType: type })
  hostEl.value?.setPointerCapture?.(event.pointerId)
  return true
}

function onConnectionMove(event: PointerEvent): void {
  if (!connection.value) return
  const rect = hostEl.value?.getBoundingClientRect()
  if (!rect) return
  connection.value = {
    ...connection.value,
    pointer: screenToFlow(viewport.value, { x: event.clientX - rect.left, y: event.clientY - rect.top }),
  }
}

function onConnectionUp(event: PointerEvent): void {
  const active = connection.value
  if (!active) return
  const origin = { nodeId: active.nodeId, handleId: active.handleId, type: active.type }
  const target = handleAt(event.clientX, event.clientY, origin)
  const found = buildConnection(origin, target)
  const valid = found ? (props.isValidConnection?.(found) ?? true) : false
  if (found && valid) {
    emit('connect', found)
  } else if (found && import.meta.env?.DEV !== false) {
    /*
     * ⚠️ 开发期诊断：**落在端口上了但没接成**。
     * 不打印的话，用户看到的是"松手之后弹了候选菜单"，
     * 而原因可能是四种（宿主判非法 / 端口识别错 / 同节点 / 方向不对），
     * 从现象上完全分不开。只在失败路径上打，正常路径零开销。
     */
    console.warn(
      '[node-print] 松手落在端口上但没接成（宿主判非法）：',
      `${origin.nodeId}.${origin.handleId} → ${found.target}.${found.targetHandle}`,
      '\n  这一条会被宿主按规则拒绝（已存在 / 类型不兼容 / 成环 / 一个输入只接一条）。',
    )
  }
  store.endConnection()
  emit('connect-end')
  hostEl.value?.releasePointerCapture?.(event.pointerId)
}

/**
 * 把"从哪来 + 落到哪"拼成一条连接。
 *
 * ⚠️ 方向**按端口类型归一**，不是按谁先按下：从输入端口往上拖
 * （改接 / 反向拉线）时，用户心里的"源"是他松手的那个输出端口。
 * 不归一的话会建出一条 target → source 的边，`linkRules` 直接判非法。
 */
function buildConnection(
  origin: { nodeId: string; handleId: string; type: 'source' | 'target' },
  target: HandleUnder,
): FlowConnection | null {
  if (!target) return null
  if (target.nodeId === origin.nodeId && target.handleId === origin.handleId) return null
  const source = origin.type === 'source' ? origin : target
  const sink = origin.type === 'source' ? target : origin
  if (source.type !== 'source' || sink.type !== 'target') return null
  if (source.nodeId === sink.nodeId) return null
  return {
    source: source.nodeId,
    sourceHandle: source.handleId,
    target: sink.nodeId,
    targetHandle: sink.handleId,
  }
}

/* ══ 右键菜单 / 双击 ══════════════════════════════════════════════ */

function onContextMenu(event: MouseEvent): void {
  const hit = hitAt(event as unknown as PointerEvent)
  if (hit.kind === 'node') {
    const node = props.nodes.find((item) => item.id === hit.id)
    if (node) emit('node-context-menu', { node, event })
    return
  }
  if (hit.kind === 'edge') {
    const edge = props.edges.find((item) => item.id === hit.id)
    if (edge) emit('edge-context-menu', { edge, event })
    return
  }
  if (hit.kind === 'other') return

  /*
   * 空白处右键 —— 两种菜单，判据是**落点在不在选中区里**。
   *
   * ⚠️ 这一条是照引擎的真实结构来的（`vue-flow-core.mjs:8151 / 8273`）：
   * 引擎在选中的节点上面铺了一层**包围盒**（`.vue-flow__nodes-selection`），
   * 它的 `contextmenu` 发 `selectionContextMenu`；真正落在 pane 上的发
   * `paneContextMenu`。**不是**"有选中就发 selection"。
   *
   * 第一版我写成了后者，于是 mount-app 里第二次右键（那时刚加完一个节点、
   * 它是选中的）弹出的是"对选中的这批"菜单，而测试找的是「添加资产」——
   * 五条端到端断言一起红。那种 bug 只有把应用真跑起来才看得见。
   */
  const rect = hostEl.value?.getBoundingClientRect()
  const flow = rect
    ? screenToFlow(viewport.value, {
        x: (Number.isFinite(event.clientX) ? event.clientX : rect.left) - rect.left,
        y: (Number.isFinite(event.clientY) ? event.clientY : rect.top) - rect.top,
      })
    : null
  const box = selectionBounds()
  if (flow && box && rectContainsPoint(box, flow)) {
    emit('selection-context-menu', event)
    return
  }
  emit('pane-context-menu', event)
}

/** 选中节点的包围盒（画布坐标）——没有选中就是 null */
function selectionBounds(): { x: number; y: number; width: number; height: number } | null {
  const selected = props.nodes.filter((node) => node.selected)
  if (selected.length === 0) return null
  const boxes = selected.map((node) => {
    const size = store.dimensionsOf(node.id) ?? { width: 0, height: 0 }
    return {
      x: node.position.x,
      y: node.position.y,
      width: size.width,
      height: size.height,
    }
  })
  const left = Math.min(...boxes.map((b) => b.x))
  const top = Math.min(...boxes.map((b) => b.y))
  const right = Math.max(...boxes.map((b) => b.x + b.width))
  const bottom = Math.max(...boxes.map((b) => b.y + b.height))
  return { x: left, y: top, width: right - left, height: bottom - top }
}

function onDoubleClick(event: MouseEvent): void {
  const hit = hitAt(event as unknown as PointerEvent)
  if (hit.kind !== 'edge') return
  const edge = props.edges.find((item) => item.id === hit.id)
  if (edge) emit('edge-double-click', { edge, event })
}

/* ══ 边层 ═════════════════════════════════════════════════════════ */

/**
 * 每条边的 `d`（画布坐标）。
 *
 * 端点 = 节点位置 + **量出来的**端口偏移。量不到时退化：
 *   ① 有尺寸 → 按"输出在右、输入在左"取边中点
 *   ② 连尺寸都没有 → 节点左上角（线会短一截，但不会崩、也不会甩到画布外）
 */
const renderedEdges = computed(() =>
  props.edges.map((edge) => {
    const sourceNode = props.nodes.find((node) => node.id === edge.source)
    const targetNode = props.nodes.find((node) => node.id === edge.target)
    if (!sourceNode || !targetNode) {
      return { id: edge.id, d: '', style: styleOfEdge(edge), selected: Boolean(edge.selected) }
    }
    const points = connectorPoints(
      { position: sourceNode.position, sockets: socketsWithFallback(sourceNode, 'right') },
      edge.sourceHandle,
      { position: targetNode.position, sockets: socketsWithFallback(targetNode, 'left') },
      edge.targetHandle,
    )
    return {
      id: edge.id,
      d: bezierPath(points.source, points.target),
      style: styleOfEdge(edge),
      selected: Boolean(edge.selected),
    }
  }),
)

function socketsWithFallback(node: FlowNode, side: 'left' | 'right'): Record<string, Point> {
  const measured = store.socketsOf(node.id) ?? {}
  const size = store.dimensionsOf(node.id)
  const fallback: Point = size
    ? { x: side === 'left' ? 0 : size.width, y: size.height / 2 }
    : { x: 0, y: 0 }
  /* 量到的端口优先；没有的那个 handle 用兜底点（Proxy 风格：按 key 现算） */
  return new Proxy(measured, {
    get: (target, key: string) => target[key] ?? fallback,
    has: () => true,
  }) as Record<string, Point>
}

function styleOfEdge(edge: FlowEdge): Record<string, string> {
  const style = edge.style as { stroke?: string; strokeDasharray?: string | number } | undefined
  return {
    stroke: style?.stroke ?? 'var(--np-edge, #6b7280)',
    strokeDasharray: String(style?.strokeDasharray ?? 'none'),
  }
}

/** 正在拖的那条预览线 */
const connectionPath = computed(() => {
  const active = connection.value
  if (!active || !active.pointer) return ''
  const node = props.nodes.find((item) => item.id === active.nodeId)
  if (!node) return ''
  const measured = store.socketsOf(node.id)?.[active.handleId]
  const size = store.dimensionsOf(node.id)
  const origin: Point = measured
    ? { x: node.position.x + measured.x, y: node.position.y + measured.y }
    : {
        x: node.position.x + (active.side === 'left' ? 0 : (size?.width ?? 0)),
        y: node.position.y + (size?.height ?? 0) / 2,
      }
  return active.type === 'source'
    ? bezierPath(origin, active.pointer)
    : bezierPath(active.pointer, origin)
})

/* ══ 生命周期 ═════════════════════════════════════════════════════ */

let resizeObserver: ResizeObserver | null = null
let mutationObserver: MutationObserver | null = null
/** 一帧内只测量一次（`ResizeObserver` 的多次触发合并掉） */
let measureFrame = 0

/** 排队测量 —— **不读布局**，所以可以放心在观察者回调里调（见 `onMounted` 的注释） */
function scheduleMeasureHost(): void {
  if (measureFrame) return
  measureFrame = requestAnimationFrame(() => {
    measureFrame = 0
    measureHost()
    /* 容器尺寸变了 → 节点换行 / 折叠都可能变，端口位置要重量 */
    store.updateNodeInternals()
  })
}

/**
 * ⚠️ **画布自己盯着自己的 DOM**（不自找的复杂度，是真机反馈逼出来的）。
 *
 * 端口位置只能从 DOM 量，而"DOM 变了"这件事**宿主不一定知道**：
 *
 * ```text
 *   · 宿主在 24 个地方调 `updateNodeInternals(...)` —— 但那调的是
 *     **焦点窗口**的 store（`flowStore` = `flowStores.get(focusedId)`）。
 *     用户在右边窗口操作、焦点判定慢一拍时，量的是**另一个窗口** ——
 *     于是一个窗口的端口偏移永远停在旧值，那里的连线就"不在端口上"。
 *   · 行结构变化（变参行增删、折叠、换行）也会动端口位置，
 *     而那不是"节点列表变了"，上面那个 watch 看不见。
 * ```
 *
 * 所以这里挂一个 `MutationObserver`：**画布内部 DOM 一有增删就重排一次测量**。
 * 于是"量得对不对"不再依赖宿主记得调用哪个 store —— 那是接线问题，
 * 不该是正确性的前提。
 *
 * ⚠️ 观察范围只要 `childList`：`attributes` / `characterData` 会在选中、
 * 悬停、输入时疯狂触发（那些都不改端口位置），而增删节点/行/端口才是
 * 真正会改几何的事件。
 *
 * ⚠️ 不会自激：测量只在**值真的变了**时才写状态（`handleGeometry` 的容差），
 * 写完最多再触发一轮"值没变→不写"，然后停。这条路径在
 * `verify:canvas-dom` 里有断言（改行结构 → 不调 store 也自己补上测量）。
 */
function watchCanvasDom(): void {
  if (typeof MutationObserver === 'undefined' || !hostEl.value) return
  mutationObserver = new MutationObserver(() => scheduleMeasureHost())
  mutationObserver.observe(hostEl.value, { childList: true, subtree: true })
}

onMounted(() => {
  measureHost()
  store.setViewport({ ...props.defaultViewport })
  emit('init', store)

  if (typeof ResizeObserver !== 'undefined' && hostEl.value) {
    /*
     * ⚠️ **回调里只排队，不测量。**
     *
     * 直接在回调里读布局 + 写状态会形成反馈环：读 → 写 → 重渲染 →
     * 尺寸又变 → 再触发观察 …… 拖窗口时每帧循环、主线程占满
     * （这个仓库真机上踩过：调整窗口宽度直接卡死）。
     * 所以测量统一走 `requestAnimationFrame`，顺带把一帧内的多次触发合并成一次。
     */
    resizeObserver = new ResizeObserver(() => scheduleMeasureHost())
    resizeObserver.observe(hostEl.value)
  }
  watchCanvasDom()
  /* 首帧之后量一次：那一刻节点才有真实尺寸 */
  nextTick(() => store.updateNodeInternals())
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  mutationObserver?.disconnect()
  mutationObserver = null
  if (measureFrame) cancelAnimationFrame(measureFrame)
  measureFrame = 0
})

/*
 * 节点变了就重量一次。
 *
 * ⚠️ `flush: 'post'`：要在 DOM 更新**之后**量 —— 默认的 `pre` 量到的是
 * 上一帧的盒子，表现是"改了一行之后连线要等下一次交互才对上"。
 */
watch(
  () => props.nodes.map((node) => `${node.id}:${node.data?.collapsed ? 1 : 0}`).join('|'),
  () => nextTick(() => store.updateNodeInternals()),
  { flush: 'post' },
)

/* 宿主写 `userSelectionActive = false`（第二根手指落下）→ 取消框选 */
watch(selectionActive, (active) => {
  if (!active && interaction.value.phase === 'selecting') {
    interaction.value = IDLE_INTERACTION
  }
})

const box = computed(() => (interaction.value.phase === 'selecting' ? normalizedBox(interaction.value) : null))

defineExpose({ store })
</script>

<template>
  <!--
    ⚠️ `@dragstart.prevent`：画布里**没有**原生拖放（全都是 pointer 手势），
    所以一律拦掉。不拦的话，拖动一段被选中的文字 / 一张预览图会触发浏览器
    自己的拖拽（Chrome 拖文字会开搜索或新标签）—— 真机反馈的那个现象。
    控件上的拖拽（数值控件左右拖）走的是 pointer 事件，不受影响。

    ⚠️ 注释只能写成 HTML 注释（像这段）。**在标签的属性列表里写 `/* */`
    是不行的** —— 模板里它不算注释，Vue 会把里面的字当成属性解析，
    报一串"属性重复"、行号还指到奇怪的地方。这条我自己就犯过两次。
  -->
  <div
    ref="hostEl"
    class="np-graph__canvas"
    @pointerdown="
      (ev) => {
        /* 端口优先：命中端口就是起线，不起框选（否则永远拉不出线） */
        if (!onHandlePointerDown(ev)) onPointerDown(ev)
      }
    "
    @pointermove="
      (ev) => {
        onConnectionMove(ev)
        onPointerMove(ev)
      }
    "
    @pointerup="
      (ev) => {
        onConnectionUp(ev)
        onPointerUp(ev)
      }
    "
    @pointercancel="onPointerCancel"
    @wheel.prevent="onWheel"
    @contextmenu.prevent="onContextMenu"
    @dblclick="onDoubleClick"
    @dragstart.prevent
  >
    <NpCanvasBackground
      v-if="background"
      :viewport="viewport"
      :gap="backgroundGap"
      :size="backgroundSize"
      :color="backgroundDot"
    />

    <div class="np-graph__viewport" :style="viewportStyle">
      <!--
        边层：放在**变换层里面**，于是路径直接用画布坐标写 `d`，
        不用每帧换算。`overflow: visible` 让 0×0 的 svg 也能画出内容。

        DOM 结构照契约来（`graphDom.edge` / `edgePath`）：外层 `<g data-id>`
        是"一条边"，里面两条同形状的路径 —— 可见的那条 + **透明加粗的命中区**
        （1.6px 的线用鼠标点很费劲，触摸端更甚）。
        剪线 / 右键命中都按这两个类名找元素，所以顺序和类名都不能变。
      -->
      <svg class="np-graph__edges" width="1" height="1" aria-hidden="true">
        <g
          v-for="edge in renderedEdges"
          :key="edge.id"
          class="np-graph__edge"
          :class="{ 'is-selected': edge.selected }"
          :data-id="edge.id"
        >
          <path class="np-graph__edge-hit" :d="edge.d" />
          <path class="np-graph__edge-path" :d="edge.d" :style="edge.style" />
        </g>
        <path v-if="connectionPath" class="np-graph__connection" :d="connectionPath" />
      </svg>

      <NpCanvasNode
        v-for="node in nodes"
        :key="node.id"
        :id="node.id"
        :position="node.position"
        :selected="Boolean(node.selected)"
        :dragging="interaction.phase === 'dragging' && interaction.nodeIds.includes(node.id)"
        :z="zIndexOf(node)"
        :extra-style="node.style"
      >
        <slot
          name="node-np"
          :id="node.id"
          :data="node.data"
          :selected="Boolean(node.selected)"
          :node="node"
        />
      </NpCanvasNode>

      <div v-if="box" class="np-graph__selection" :style="{ left: `${box.x}px`, top: `${box.y}px`, width: `${box.width}px`, height: `${box.height}px` }" />
    </div>

    <!-- 应用层的浮层（空状态提示、小地图折叠按钮…） -->
    <slot />

    <NpCanvasMiniMap
      v-if="minimap"
      :viewport="viewport"
      :nodes="nodes"
      :dimensions-of="(id) => store.dimensionsOf(id)"
      :canvas-size="hostSize"
      :width="minimapSize.width"
      :height="minimapSize.height"
      :node-color="minimapNodeColor"
      :node-stroke="minimapNodeStroke"
      :mask-color="minimapMaskColor"
      @pointerdown="(event: PointerEvent) => emit('minimap-pointer-down', event)"
      @pointermove="(event: PointerEvent) => emit('minimap-pointer-move', event)"
      @pointerup="(event: PointerEvent) => emit('minimap-pointer-up', event)"
      @pointercancel="(event: PointerEvent) => emit('minimap-pointer-cancel', event)"
    />
  </div>
</template>
